import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, AdminDfeLegacyImportEntry } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useArcheAdmin } from '@/auth/hooks/useArcheAdmin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export function AdminImportsPage() {
  // Server route gate: `requireAdminOrLitcraftAdmin` (docs/api-admin.md).
  const isAdmin = useArcheAdmin('litcraft_admin');
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<AdminDfeLegacyImportEntry | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.inventoryTransition.adminImports(),
    queryFn: () => api.listAllDfeLegacyImports(),
    enabled: isAdmin,
  });

  const deleteMutation = useMutation({
    mutationFn: (importId: string) => api.deleteDfeLegacyImport(importId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryTransition.all });
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.importId);
    deleteMutation.mutate(deleteTarget.importId);
    setDeleteTarget(null);
  };

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <p className="text-muted-foreground">Admin access required.</p>
      </div>
    );
  }

  const imports: AdminDfeLegacyImportEntry[] = data?.imports ?? [];

  return (
    <div className="max-w-5xl mx-auto space-y-6 p-4">
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
          <Link to="/a/arche_dfe_inventory_transition">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Inventory Transition
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Legacy Import Admin</h1>
        <p className="text-muted-foreground mt-1">
          Manage legacy import records. Deleting a record allows that legacy account to be
          re-imported.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Import Records</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : imports.length === 0 ? (
            <p className="text-sm text-muted-foreground">No import records found.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Legacy Username</TableHead>
                    <TableHead>Fias User</TableHead>
                    <TableHead>Imported</TableHead>
                    <TableHead>Skipped</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {imports.map((imp) => (
                    <TableRow key={imp.importId}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {new Date(imp.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-mono text-sm whitespace-nowrap">
                        {imp.legacyUsername}
                      </TableCell>
                      <TableCell className="text-sm">
                        {imp.fiasUsername ? (
                          <span title={imp.fiasUserId}>{imp.fiasUsername}</span>
                        ) : (
                          <span
                            className="font-mono text-xs text-muted-foreground"
                            title={imp.fiasUserId}
                          >
                            {imp.fiasUserId}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{imp.itemsImported}</TableCell>
                      <TableCell>{imp.itemsSkipped}</TableCell>
                      <TableCell>
                        <Badge
                          variant={imp.importStatus === 'completed' ? 'default' : 'destructive'}
                        >
                          {imp.importStatus}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={deletingId === imp.importId}
                          onClick={() => setDeleteTarget(imp)}
                        >
                          {deletingId === imp.importId ? 'Deleting...' : 'Delete'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete import record?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the import record for legacy account &ldquo;
              {deleteTarget?.legacyUsername}&rdquo;, allowing it to be re-imported. The previously
              imported items will not be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
